import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import "./enterprise-patterns.css";
import "./enrichment-patterns.css";
import { Button as MuiButton, Chip, CssBaseline, Paper, ThemeProvider, } from "@mui/material";
import { createContext, useContext, useMemo, } from "react";
import { tokens } from "@inteacc/design-tokens";
import { currentThemeSchemaVersion, currentThemeVersion, defaultThemeSources, resolveTheme, } from "@inteacc/theme";
import { projectResolvedTheme } from "./theme-adapter";
import { ActionIcon, resolveActionIcon } from "./action-icons";
export { ActionIcon, resolveActionIcon };
export const Button = ({ ariaLabel, icon, children, disabled = false, onClick, tone = "primary", type = "button", }) => (_jsx(MuiButton, { "aria-label": ariaLabel, color: tone === "danger" ? "error" : tone === "neutral" ? "inherit" : "primary", disabled: disabled, onClick: onClick, type: type, variant: tone === "neutral" ? "outlined" : "contained", startIcon: icon === false ? undefined : (() => {
        const resolved = icon ?? (typeof children === "string" ? resolveActionIcon(children) : undefined);
        return resolved ? _jsx(ActionIcon, { name: resolved }) : undefined;
    })(), children: children }));
export const StatusBadge = ({ label, tone = "neutral", }) => (_jsx(Chip, { color: tone === "neutral"
        ? "default"
        : tone === "danger"
            ? "error"
            : tone, label: label, size: "small" }));
export const Surface = ({ children, heading }) => (_jsxs(Paper, { component: "section", elevation: 0, sx: {
        border: "1px solid var(--iaf-color-neutral-100)",
        borderRadius: "var(--iaf-radius-medium)",
        background: "var(--iaf-color-neutral-0)",
        color: "var(--iaf-color-neutral-900)",
        p: 3,
    }, children: [heading ? _jsx("h2", { children: heading }) : null, children] }));
/** Reusable density choices for application preference controls. */
export const iafDensityOptions = [
    { value: "compact", label: "Compact" },
    { value: "standard", label: "Standard" },
    { value: "comfortable", label: "Comfortable" },
];
/** Controlled preference input; pass the selection to your theme preference provider. */
export const DensitySelect = ({ value, onChange, label = "Density", }) => (_jsxs("label", { children: [_jsx("span", { children: label }), _jsx("select", { "aria-label": label, value: value, onChange: (event) => onChange(event.target.value), children: iafDensityOptions.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value))) })] }));
export const defaultIafThemePreferences = {
    themeId: "inteacc-blue",
    mode: "light",
    density: "standard",
    brandColor: tokens.color.brand[500],
    accentColor: tokens.color.semantic.info,
    fontScale: 1,
    contrast: "standard",
    corners: "soft",
};
export const isValidThemeColor = (color) => /^#[0-9a-f]{6}$/i.test(color);
const colorChannels = (color) => [
    Number.parseInt(color.slice(1, 3), 16),
    Number.parseInt(color.slice(3, 5), 16),
    Number.parseInt(color.slice(5, 7), 16),
];
const relativeLuminance = (color) => {
    const channels = colorChannels(color).map((value) => {
        const normalized = value / 255;
        return normalized <= 0.03928
            ? normalized / 12.92
            : ((normalized + 0.055) / 1.055) ** 2.4;
    });
    const [red = 0, green = 0, blue = 0] = channels;
    return red * 0.2126 + green * 0.7152 + blue * 0.0722;
};
export const themeContrastRatio = (foreground, background) => {
    if (!isValidThemeColor(foreground) || !isValidThemeColor(background)) {
        return 0;
    }
    const foregroundLuminance = relativeLuminance(foreground);
    const backgroundLuminance = relativeLuminance(background);
    const lighter = Math.max(foregroundLuminance, backgroundLuminance);
    const darker = Math.min(foregroundLuminance, backgroundLuminance);
    return (lighter + 0.05) / (darker + 0.05);
};
const resolvedThemeContext = createContext(undefined);
export const useInteaccResolvedTheme = () => useContext(resolvedThemeContext);
const legacyPreferencesToResolvedTheme = (preferences, mode, density) => {
    const timestamp = "1970-01-01T00:00:00.000Z";
    const active = preferences ?? {
        ...defaultIafThemePreferences,
        mode,
        density,
    };
    const preference = {
        key: `legacy:${active.themeId}`,
        scope: {
            kind: "preview",
            productId: "legacy-ui",
            tenantId: "local",
            userId: "local",
            previewId: active.themeId,
        },
        schemaVersion: currentThemeSchemaVersion,
        themeVersion: currentThemeVersion,
        values: {
            "appearance.mode": active.mode,
            "density.mode": active.density,
            "brand.primary": isValidThemeColor(active.brandColor)
                ? active.brandColor
                : defaultIafThemePreferences.brandColor,
            "brand.secondary": isValidThemeColor(active.accentColor)
                ? active.accentColor
                : defaultIafThemePreferences.accentColor,
            "color.status.information": isValidThemeColor(active.accentColor)
                ? active.accentColor
                : defaultIafThemePreferences.accentColor,
            "typography.scale": Math.min(1.25, Math.max(0.875, active.fontScale)),
            "appearance.contrastMode": active.contrast,
            "accessibility.highContrast": active.contrast === "high",
            "shape.radius.small": active.corners === "square" ? 0 : active.corners === "rounded" ? 12 : 6,
            "shape.radius.medium": active.corners === "square" ? 0 : active.corners === "rounded" ? 16 : 10,
            "shape.radius.large": active.corners === "square" ? 0 : active.corners === "rounded" ? 22 : 16,
        },
        compatibility: {
            schemaVersion: currentThemeSchemaVersion,
            themeVersion: currentThemeVersion,
            minimumSupportedIafVersion: "0.1.0",
            migrationStatus: "not-required",
        },
        revision: 0,
        createdBy: "legacy-ui-adapter",
        createdAt: timestamp,
        updatedBy: "legacy-ui-adapter",
        updatedAt: timestamp,
    };
    return resolveTheme({
        productId: "legacy-ui",
        tenantId: "local",
        userId: "local",
        iafVersion: "0.1.0",
        resolvedAt: timestamp,
        environment: {
            prefersDark: mode === "dark",
            prefersReducedMotion: false,
            prefersHighContrast: false,
            forcedColorsActive: false,
        },
        sources: [
            ...defaultThemeSources,
            {
                sourceId: preference.key,
                layer: "preview",
                scope: preference.scope,
                preference,
            },
        ],
    }).theme;
};
export const InteaccThemeProvider = ({ children, density = "standard", mode = "light", preferences, resolvedTheme, }) => {
    const activeTheme = useMemo(() => resolvedTheme ??
        legacyPreferencesToResolvedTheme(preferences, mode, density), [density, mode, preferences, resolvedTheme]);
    const projection = useMemo(() => projectResolvedTheme(activeTheme), [activeTheme]);
    return (_jsx(resolvedThemeContext.Provider, { value: activeTheme, children: _jsxs(ThemeProvider, { theme: projection.muiTheme, children: [_jsx(CssBaseline, {}), _jsx("div", { "data-iaf-theme": preferences?.themeId ?? activeTheme.fingerprint, "data-iaf-theme-mode": activeTheme.mode, "data-iaf-visualization-hover": activeTheme.values["motion.enabled"] &&
                        !activeTheme.values["motion.reduced"] &&
                        !activeTheme.values["accessibility.reducedMotion"]
                        ? activeTheme.values["visualization.hoverEffect"] : "none", "data-iaf-visualization-style": activeTheme.values["visualization.cardStyle"], "data-iaf-button-style": activeTheme.values["ui.buttonStyle"], "data-iaf-form-style": activeTheme.values["ui.formStyle"], "data-iaf-table-style": activeTheme.values["ui.tableStyle"], "data-iaf-navigation-style": activeTheme.values["ui.navigationStyle"], style: projection.cssVariables, children: children })] }) }));
};
export { AttachmentPicker, CommandPalette, FeedbackCenter, OperationProgress, RecordComparison, SkeletonWorkspace, WorkspaceOverlay, } from "./enterprise-patterns";
export { ConditionalFieldGroup, EnterpriseGrid } from "./enterprise-patterns";
export { ActivityTimeline, ContextActionMenu, DashboardLayout, evaluateFilterGroup, FilterBuilder, FormWizard, PrintDocument, RelatedRecordPanel, } from "./enrichment-patterns";
export { IafCopyProvider, useIafCopy } from "./copy";
//# sourceMappingURL=index.js.map